import { RadioButton } from '@lt/components';
import { useTranslation } from '@lt/localization';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { JOINT_STATES } from '../../constants';
import { isAdditionalOrder, isFirstOrder } from '../../utils';
import OrderNumberForm from './components/OrderNumberForm';
import OrdersAmountForm from './components/OrdersAmountForm';
import { TEST_ID_ADDITIONAL_ORDER, TEST_ID_FIRST_ORDER } from './constants';
import type { JointProps } from '../../types';
import type { PropsFromRedux } from '../..';

type Props = JointProps & PropsFromRedux;

const SwitchOrder = (props: Props): JSX.Element => {
  const {
    jointState,
    setJointState,
    isMobile,
    firstOrder,
    setFirstOrder,
    ordersAmount,
    setOrdersAmount,
    switchOrder,
    setFirstOrderField,
  } = props;
  const { t } = useTranslation();
  const firstOrderTitle = t('checkout.services.jointOrder.firstOrder.title');
  const addOrderTitle = t('checkout.services.jointOrder.additionalOrder.title');
  const firstOrderDescription = t(
    'checkout.services.jointOrder.firstOrder.description',
  );
  const handleFirstOrderClicked = () => {
    setJointState(JOINT_STATES.FIRST_ORDER);
    switchOrder();
  };
  const handleAdditionalOrderClicked = () => {
    setJointState(JOINT_STATES.ADDITIONAL_ORDER);
    switchOrder();
  };
  useEffect(() => {
    setFirstOrderField(true);
    return () => {
      setFirstOrderField(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <StyledSwitchOrderContainer>
      <StyledFirstOrderContainer>
        <StyledRadioContainer $additional={false} $isMobile={isMobile}>
          <RadioButton
            value={jointState}
            name="firstOrder"
            checked={isFirstOrder(jointState)}
            onClick={handleFirstOrderClicked}
          />
          <OrderTitle>{firstOrderTitle}</OrderTitle>
        </StyledRadioContainer>
        {isFirstOrder(jointState) && (
          <>
            <OrderDescription
              data-testid={TEST_ID_FIRST_ORDER}
              $isMobile={isMobile}
            >
              {firstOrderDescription}
            </OrderDescription>
            <OrdersAmountForm
              fieldValue={ordersAmount}
              setFieldValue={setOrdersAmount}
              isMobile={isMobile}
            />
          </>
        )}
      </StyledFirstOrderContainer>
      <StyledAdditionalOrderContainer
        $isAdditional={isAdditionalOrder(jointState)}
      >
        <StyledRadioContainer $additional $isMobile={isMobile}>
          <RadioButton
            value={jointState}
            name="additionalOrder"
            checked={isAdditionalOrder(jointState)}
            onClick={handleAdditionalOrderClicked}
          />
          <OrderTitle>{addOrderTitle}</OrderTitle>
        </StyledRadioContainer>
        {isAdditionalOrder(jointState) && (
          <StyledInputContainer data-testid={TEST_ID_ADDITIONAL_ORDER}>
            <OrderNumberForm
              fieldValue={firstOrder}
              setFieldValue={setFirstOrder}
            />
          </StyledInputContainer>
        )}
      </StyledAdditionalOrderContainer>
    </StyledSwitchOrderContainer>
  );
};
const StyledSwitchOrderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  align-self: stretch;
  border-radius: 4px;
  background: #f5f3f2;
`;

const StyledFirstOrderContainer = styled.div`
  display: flex;
  padding: 12px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-bottom: 1px solid #d6d6d6;
`;
const StyledAdditionalOrderContainer = styled.div<{ $isAdditional: boolean }>`
  display: flex;
  ${({ $isAdditional }) =>
    $isAdditional ? `padding: 0px 16px 16px 16px;` : `padding: 0 16px;`}
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;
const StyledRadioContainer = styled.div<{
  $additional: boolean;
  $isMobile: boolean;
}>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  ${({ $additional }) => $additional && `padding: 12px 0;`}
  ${({ $isMobile }) =>
    $isMobile &&
    `
    flex-direction: row-reverse;
    justify-content: space-between;
  `}
`;

const OrderTitle = styled.span`
  font-size: 14px;
  line-height: 16px;
`;

const OrderDescription = styled.span<{ $isMobile: boolean }>`
  color: #4d4d4d;
  font-size: 12px;

  width: ${({ $isMobile }) => ($isMobile ? `279px` : `598px`)};
`;

const StyledInputContainer = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
`;

export default SwitchOrder;
